import classnames from "classnames";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";

import { AppState } from "shared/data/reducers";
import { currencies } from "shared/lib/currencies";
import { rawDateToUrlDate, urlDateToRawDate } from "shared/lib/functions";
import {
  PriceCalendarDays,
  PriceCalendarOptions,
  PriceCalendarRequest,
  PriceCalendarSelectionMode,
  PriceCalendarTier,
  fetchPriceCalendar,
  formatPriceCalendarAmount,
  formatPriceCalendarPrice,
  getPriceCalendarPeriod,
  getPriceCalendarRequestConfig,
  getPriceCalendarTiers,
} from "shared/lib/priceCalendar";

import TripDateCalendar from "@components/Calendar/TripDateCalendar";

export interface PriceCalendarCache {
  outbound: Record<string, Promise<PriceCalendarDays>>;
  returns: Record<string, Promise<PriceCalendarDays>>;
}

interface Props {
  value: any;
  onChange: (string) => void;
  monthsToAdd?: number;
  disabledTillDate: string;
  disabledAfterDate?: string;
  priceCalendar?: PriceCalendarOptions;
  rangeSelectionEnabled?: boolean;
  rangeDepartureDate?: string;
  rangeReturnDate?: string;
  onRangeDepartureChange?: (value: string) => void;
  onRangeReturnChange?: (value: string) => void;
  onSelectionModeChange?: (value: PriceCalendarSelectionMode) => void;
  initialSelectionMode?: PriceCalendarSelectionMode;
  priceCalendarCache?: React.MutableRefObject<PriceCalendarCache>;
}

const constructDate = (month, year) => {
  return new Date(year, month, 1, 0, 0, 0);
};

const DAYS_IN_YEAR = 365;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

const getStartOfDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0);

const parseCalendarDate = (value?: string | Date): Date | null => {
  if (!value) {
    return null;
  }

  const date =
    value instanceof Date
      ? value
      : /^\d{4}-\d{2}-\d{2}$/.test(value)
      ? urlDateToRawDate(value)
      : new Date(value);

  return Number.isNaN(date.getTime()) ? null : getStartOfDay(date);
};

const addDays = (date: Date, days: number): Date => {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + days);

  return nextDate;
};

const buildAvailableDates = (from: Date, to: Date): Date[] => {
  if (from > to) {
    return [];
  }

  const dayCount = Math.floor((to.getTime() - from.getTime()) / MS_PER_DAY) + 1;

  return Array.from({ length: dayCount }, (_, index) => addDays(from, index));
};

const getNextYearDate = () => addDays(getStartOfDay(new Date()), DAYS_IN_YEAR);

const getInitialVisibleMonth = (value: string, monthsToAdd = 0): Date => {
  const initialDate = value ? urlDateToRawDate(value) : new Date();
  initialDate.setMonth(initialDate.getMonth() + monthsToAdd);

  return constructDate(initialDate.getMonth(), initialDate.getFullYear());
};

const getReturnDateLabel = (
  language: string,
  formatMessage: ReturnType<typeof useIntl>["formatMessage"]
) =>
  language === "cs"
    ? "Přílet"
    : formatMessage({ id: "FlightAdditionalInfo.return" });

const getPriceCalendarTextStyle = (amount: string): React.CSSProperties => {
  const estimatedWidth = Math.max(amount.length * 0.55, 1);
  const fontSize = Math.max(6, Math.min(10, 48 / estimatedWidth));

  return {
    ["--price-calendar-day-price-font-size" as string]: `${fontSize.toFixed(
      2
    )}px`,
  };
};

interface PriceCalendarDayPriceProps {
  amount: string;
  currency: string;
  priceText: string;
  title: string;
  priceTier?: PriceCalendarTier;
}

const PriceCalendarDayPrice = ({
  amount,
  currency,
  priceText,
  title,
  priceTier,
}: PriceCalendarDayPriceProps): React.ReactElement => {
  const priceRef = useRef<HTMLSpanElement>(null);
  const measurementRef = useRef<HTMLSpanElement>(null);
  const [showCurrency, setShowCurrency] = useState(Boolean(currency));

  useEffect(() => {
    const updateCurrencyVisibility = () => {
      const availableWidth = priceRef.current?.clientWidth || 0;
      const requiredWidth = measurementRef.current?.scrollWidth || 0;

      setShowCurrency(
        Boolean(currency && availableWidth && requiredWidth <= availableWidth)
      );
    };

    updateCurrencyVisibility();

    if (!priceRef.current || typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateCurrencyVisibility);

      return () =>
        window.removeEventListener("resize", updateCurrencyVisibility);
    }

    const resizeObserver = new ResizeObserver(updateCurrencyVisibility);
    resizeObserver.observe(priceRef.current);

    return () => resizeObserver.disconnect();
  }, [amount, currency]);

  return (
    <span
      ref={priceRef}
      className={classnames("price-calendar-day-price", {
        "is-medium": priceTier === "medium",
        "is-expensive": priceTier === "expensive",
      })}
      aria-label={priceText}
      data-currency-hidden={currency && !showCurrency ? "true" : undefined}
      style={getPriceCalendarTextStyle(amount)}
      title={title}
    >
      <span className="price-calendar-day-price-amount">{amount}</span>
      {showCurrency && (
        <span className="price-calendar-day-price-currency">{currency}</span>
      )}
      <span
        ref={measurementRef}
        aria-hidden="true"
        className="price-calendar-day-price-measure"
      >
        <span className="price-calendar-day-price-amount">{amount}</span>
        {currency && (
          <span className="price-calendar-day-price-currency">{currency}</span>
        )}
      </span>
    </span>
  );
};

const getPriceCalendarRequestCacheKey = (
  request: PriceCalendarRequest
): string => JSON.stringify(getPriceCalendarRequestConfig(request));

const getCachedPriceCalendar = (
  cache: Record<string, Promise<PriceCalendarDays>>,
  cacheKey: string,
  request: PriceCalendarRequest
): Promise<PriceCalendarDays> => {
  const cachedRequest = cache[cacheKey];

  if (cachedRequest) {
    return cachedRequest;
  }

  const requestPromise = fetchPriceCalendar(request);
  const requestCache = cache;
  requestCache[cacheKey] = requestPromise;

  return requestPromise;
};

const CalendarMonth = (props: Props): React.ReactElement => {
  const intl = useIntl();
  const currentLanguage = useSelector(
    (state: AppState) => state.requestorConfig.currentLanguage
  );
  const [visibleMonth, setVisibleMonth] = useState(() =>
    getInitialVisibleMonth(props.value, props.monthsToAdd)
  );
  const [activeSelectionMode, setActiveSelectionMode] = useState(
    props.initialSelectionMode ?? PriceCalendarSelectionMode.OUTBOUND
  );
  const [rangeSelectionCompleted, setRangeSelectionCompleted] = useState(false);
  const [hoveredReturnDate, setHoveredReturnDate] = useState<string | null>(
    null
  );
  const [period, setPeriod] = useState(getPriceCalendarPeriod(visibleMonth));
  const [outboundPrices, setOutboundPrices] = useState<PriceCalendarDays>({});
  const [returnPricesCache, setReturnPricesCache] = useState<
    Record<string, PriceCalendarDays>
  >({});
  const localPriceCalendarCache = useRef<PriceCalendarCache>({
    outbound: {},
    returns: {},
  });
  const priceCalendarCache =
    props.priceCalendarCache || localPriceCalendarCache;

  useEffect(() => {
    setVisibleMonth(getInitialVisibleMonth(props.value, props.monthsToAdd));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setActiveSelectionMode(
      props.initialSelectionMode ?? PriceCalendarSelectionMode.OUTBOUND
    );
    setRangeSelectionCompleted(false);
  }, [props.initialSelectionMode]);

  useEffect(() => {
    setPeriod(getPriceCalendarPeriod(visibleMonth));
  }, [visibleMonth]);

  const rangeSelectionEnabled = Boolean(props.rangeSelectionEnabled);
  const priceCalendarEnabled = Boolean(
    props.priceCalendar?.enabled &&
      props.priceCalendar.origin &&
      props.priceCalendar.destination
  );

  const getPriceCalendarRequest = useCallback(
    (sourceDepartureDate?: string): PriceCalendarRequest | null =>
      props.priceCalendar
        ? {
            ...props.priceCalendar,
            periodStart: period.periodStart,
            periodEnd: period.periodEnd,
            sourceDepartureDate,
          }
        : null,
    [period.periodEnd, period.periodStart, props.priceCalendar]
  );

  const getReturnPricesCacheKey = useCallback(
    (outboundDate: string) => {
      const request = getPriceCalendarRequest(outboundDate);

      return request ? getPriceCalendarRequestCacheKey(request) : "";
    },
    [getPriceCalendarRequest]
  );

  const loadReturnPrices = useCallback(
    async (outboundDate: string) => {
      if (!rangeSelectionEnabled || !priceCalendarEnabled || !outboundDate) {
        return;
      }

      const request = getPriceCalendarRequest(outboundDate);

      if (!request) {
        return;
      }

      const cacheKey = getPriceCalendarRequestCacheKey(request);
      const prices = await getCachedPriceCalendar(
        priceCalendarCache.current.returns,
        cacheKey,
        request
      );

      setReturnPricesCache((prevCache) => ({
        ...prevCache,
        [cacheKey]: prices,
      }));
    },
    [
      getPriceCalendarRequest,
      priceCalendarCache,
      priceCalendarEnabled,
      rangeSelectionEnabled,
    ]
  );

  useEffect(() => {
    let isMounted = true;

    if (!priceCalendarEnabled || !props.priceCalendar) {
      setOutboundPrices({});
      setReturnPricesCache({});
      return;
    }

    const request = getPriceCalendarRequest();

    if (!request) {
      return;
    }

    const cacheKey = getPriceCalendarRequestCacheKey(request);

    getCachedPriceCalendar(
      priceCalendarCache.current.outbound,
      cacheKey,
      request
    ).then((prices) => {
      if (isMounted) {
        setOutboundPrices(prices);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [
    getPriceCalendarRequest,
    priceCalendarCache,
    priceCalendarEnabled,
    props.priceCalendar,
  ]);

  useEffect(() => {
    if (
      rangeSelectionEnabled &&
      priceCalendarEnabled &&
      activeSelectionMode === PriceCalendarSelectionMode.RETURN &&
      props.rangeDepartureDate
    ) {
      loadReturnPrices(props.rangeDepartureDate);
    }
  }, [
    activeSelectionMode,
    loadReturnPrices,
    priceCalendarEnabled,
    rangeSelectionEnabled,
    props.rangeDepartureDate,
  ]);

  const returnPrices = props.rangeDepartureDate
    ? returnPricesCache[getReturnPricesCacheKey(props.rangeDepartureDate)] || {}
    : {};

  const displayedPrices =
    activeSelectionMode === PriceCalendarSelectionMode.RETURN
      ? returnPrices
      : outboundPrices;
  const outboundPriceTiers = useMemo(
    () => getPriceCalendarTiers(outboundPrices),
    [outboundPrices]
  );

  const getDisplayedDayPrice = (date: Date) => {
    const dateKey = rawDateToUrlDate(date);

    if (
      activeSelectionMode === PriceCalendarSelectionMode.RETURN &&
      dateKey === props.rangeDepartureDate &&
      props.rangeReturnDate
    ) {
      return returnPrices[props.rangeReturnDate];
    }

    return displayedPrices[dateKey];
  };

  const handleDayClick = (
    date: Date,
    modifiers: Record<string, boolean> = {}
  ) => {
    if (modifiers.disabled) {
      return;
    }

    const dateValue = rawDateToUrlDate(date);

    if (!rangeSelectionEnabled) {
      props.onChange(dateValue);
      return;
    }

    if (
      activeSelectionMode === PriceCalendarSelectionMode.OUTBOUND ||
      rangeSelectionCompleted
    ) {
      props.onRangeDepartureChange?.(dateValue);
      props.onRangeReturnChange?.("");
      setVisibleMonth(constructDate(date.getMonth(), date.getFullYear()));
      setHoveredReturnDate(null);
      setRangeSelectionCompleted(false);
      setActiveSelectionMode(PriceCalendarSelectionMode.RETURN);
      props.onSelectionModeChange?.(PriceCalendarSelectionMode.RETURN);
      loadReturnPrices(dateValue);
      return;
    }

    if (props.rangeDepartureDate && dateValue < props.rangeDepartureDate) {
      return;
    }

    props.onRangeReturnChange?.(dateValue);
    setHoveredReturnDate(null);
    setRangeSelectionCompleted(true);
    props.onSelectionModeChange?.(PriceCalendarSelectionMode.OUTBOUND);
  };

  const today = getStartOfDay(new Date());
  const returnSelectionStartDate =
    activeSelectionMode === PriceCalendarSelectionMode.RETURN &&
    !rangeSelectionCompleted &&
    props.rangeDepartureDate
      ? parseCalendarDate(props.rangeDepartureDate)
      : null;
  const selectableStartDate =
    returnSelectionStartDate ||
    parseCalendarDate(props.disabledTillDate) ||
    today;
  const nextYearDate = getNextYearDate();
  const disabledAfterDate = parseCalendarDate(props.disabledAfterDate);
  const selectableEndDate =
    disabledAfterDate && disabledAfterDate < nextYearDate
      ? disabledAfterDate
      : nextYearDate;
  const selectableStartTime = selectableStartDate.getTime();
  const selectableEndTime = selectableEndDate.getTime();
  const availableDates = useMemo(
    () =>
      buildAvailableDates(
        new Date(selectableStartTime),
        new Date(selectableEndTime)
      ),
    [selectableEndTime, selectableStartTime]
  );

  const rangeToDate =
    activeSelectionMode === PriceCalendarSelectionMode.RETURN
      ? hoveredReturnDate || props.rangeReturnDate
      : props.rangeReturnDate;

  const rangeDates =
    rangeSelectionEnabled && props.rangeDepartureDate && rangeToDate
      ? {
          from: urlDateToRawDate(props.rangeDepartureDate),
          to: urlDateToRawDate(rangeToDate),
        }
      : undefined;

  const selectedDate = rangeSelectionEnabled
    ? [props.rangeDepartureDate, props.rangeReturnDate]
        .filter(Boolean)
        .map((date) => urlDateToRawDate(date))
    : urlDateToRawDate(props.value);

  const selectedDateLabels: Array<{
    date: Date;
    label: string;
    variant?: "roundTrip";
  }> = [];
  const departureDateLabel = intl.formatMessage({ id: "Flight.departure" });
  const returnDateLabel = getReturnDateLabel(
    currentLanguage,
    intl.formatMessage
  );

  if (
    rangeSelectionEnabled &&
    props.rangeDepartureDate &&
    props.rangeDepartureDate === props.rangeReturnDate
  ) {
    selectedDateLabels.push({
      date: urlDateToRawDate(props.rangeDepartureDate),
      label: `${departureDateLabel} / ${returnDateLabel}`,
      variant: "roundTrip",
    });
  } else {
    if (rangeSelectionEnabled && props.rangeDepartureDate) {
      selectedDateLabels.push({
        date: urlDateToRawDate(props.rangeDepartureDate),
        label: departureDateLabel,
      });
    }

    if (rangeSelectionEnabled && props.rangeReturnDate) {
      selectedDateLabels.push({
        date: urlDateToRawDate(props.rangeReturnDate),
        label: returnDateLabel,
      });
    }
  }

  const renderDayFooter = (date: Date) => {
    const priceDay = getDisplayedDayPrice(date);
    const priceTier = outboundPriceTiers[rawDateToUrlDate(date)];

    if (!priceCalendarEnabled || !priceDay) {
      return null;
    }

    const priceAmount = formatPriceCalendarAmount(priceDay);
    const priceText = formatPriceCalendarPrice(priceDay);
    const priceCurrency =
      currencies[priceDay.currency.toUpperCase()] || priceDay.currency;
    const title = [priceText, priceDay?.source, priceDay?.fareType]
      .filter(Boolean)
      .join(" | ");

    return (
      <PriceCalendarDayPrice
        amount={priceAmount}
        currency={priceCurrency}
        priceText={priceText}
        priceTier={priceTier}
        title={title}
      />
    );
  };

  const handleCalendarChange = (date: Date) => {
    setVisibleMonth(constructDate(date.getMonth(), date.getFullYear()));
  };

  const handleDayMouseEnter = (date: Date) => {
    if (
      rangeSelectionEnabled &&
      activeSelectionMode === PriceCalendarSelectionMode.RETURN &&
      !rangeSelectionCompleted
    ) {
      setHoveredReturnDate(rawDateToUrlDate(date));
    }
  };

  return (
    <div
      className={classnames("header-search-form-calendar-picker", {
        "has-price-calendar": priceCalendarEnabled,
      })}
      onMouseLeave={() => setHoveredReturnDate(null)}
    >
      <TripDateCalendar
        id="search-form-calendar"
        className={classnames({
          "has-price-calendar": priceCalendarEnabled,
        })}
        fixedWeeks
        fromMonth={rawDateToUrlDate(selectableStartDate)}
        toMonth={rawDateToUrlDate(selectableEndDate)}
        startingDate={rawDateToUrlDate(visibleMonth)}
        numberOfMonths={2}
        mobileNumberOfMonths={1}
        availableDates={availableDates}
        focusableDates={availableDates}
        highlightedDates={availableDates}
        showHighlightedDateBorders={false}
        selectedDate={selectedDate}
        selectedDateLabels={
          selectedDateLabels.length ? selectedDateLabels : undefined
        }
        rangeDates={rangeDates}
        locale={currentLanguage}
        onDayClick={handleDayClick}
        onDayMouseEnter={handleDayMouseEnter}
        onCalendarChange={handleCalendarChange}
        renderDayFooter={priceCalendarEnabled ? renderDayFooter : undefined}
      />
    </div>
  );
};

export default CalendarMonth;
